import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import _ from 'lodash';
import React from 'react';
import { getAllCompanies } from 'services/companyServices';
import { getAllCovidTestAnalyzers } from 'services/covidTestAnalyzerServices';
import { getAllInterventions } from 'services/interventionServices';
import { getAllTestKitReceptionOptions } from 'services/labCentralServices';
import { getResources } from 'services/patientCentralServices';
import { fetchAllProducts } from 'services/productServices';
import { getAllQuestionSets } from 'services/questionSetsServices';
import { getAllTariffs } from 'services/tariffServices';
import { getAllTestLaboratory } from 'services/testLaboratoryServices';
import { getAllTreatmentReasons } from 'services/treatmentReasonServices';
import { InterventionExternalCode } from 'ts-types/api.enums';
import {
  CompanyDto,
  CovidTestAnalyzerDto,
  InterventionDto,
  ProductDto,
  QuestionSetDto,
  ResourceDto,
  TariffDto,
  TestKitReceptionOptionDto,
  TestLaboratoryDto,
  TreatmentReasonDto
} from 'ts-types/api.types';

interface State {
  tariffs: TariffDto[];
  reasons: TreatmentReasonDto[];
  interventions: InterventionDto[];
  testKitReceptionOptions: TestKitReceptionOptionDto[];
  questionSets: QuestionSetDto[];
  resources: ResourceDto[];
  companies: CompanyDto[];
  products: ProductDto[];
  productMap: { [id: string]: ProductDto },
  laboratories: TestLaboratoryDto[],
  covidTestAnalyzers: CovidTestAnalyzerDto[],
  anonymousMode: boolean;
  referenceDataFetched: boolean;

  getTariffById(tariffId: number): TariffDto | undefined;

  getReasonById(reasonId: number): TreatmentReasonDto | undefined;

  getQuestionSetById(questionSetId: number): QuestionSetDto | undefined;

  getInterventionById(id: number): InterventionDto | undefined;

  getInterventionByExternalCode(externalCode: InterventionExternalCode): InterventionDto | undefined;

  getTestKitReceptionOptionById(id: number): TestKitReceptionOptionDto | undefined;

  setAnonymousMode(): void;
}

const PatientCentralRefDataContext = React.createContext({
  tariffs: [] as TariffDto[],
  reasons: [] as TreatmentReasonDto[],
  interventions: [] as InterventionDto[],
  testKitReceptionOptions: [] as TestKitReceptionOptionDto[],
  questionSets: [] as QuestionSetDto[],
  resources: [] as ResourceDto[],
  companies: [] as CompanyDto[],
  products: [] as ProductDto[],
  productMap: {} as { [id: string]: ProductDto },
  laboratories: [] as TestLaboratoryDto[],
  covidTestAnalyzers: [] as CovidTestAnalyzerDto[],
  anonymousMode: false,
  referenceDataFetched: false,
  getTariffById: (tariffId: number): TariffDto | undefined => undefined,
  getReasonById: (reasonId: number): TreatmentReasonDto | undefined => undefined,
  getQuestionSetById: (questionSetId: number): QuestionSetDto | undefined => undefined,
  getInterventionById: (id: number): InterventionDto | undefined => undefined,
  getInterventionByExternalCode: (externalCode: InterventionExternalCode): InterventionDto | undefined => undefined,
  getTestKitReceptionOptionById: (id: number): TestKitReceptionOptionDto | undefined => undefined,
  setAnonymousMode: () => {
  }
});

interface Props extends JSX.ElementChildrenAttribute, AuthConsumerRenderProps {
}

class PatientCentralRefDataContextProvider extends React.Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  constructor(props: Props) {
    super(props);

    this.state = {
      tariffs: [] as TariffDto[],
      reasons: [] as TreatmentReasonDto[],
      interventions: [] as InterventionDto[],
      testKitReceptionOptions: [] as TestKitReceptionOptionDto[],
      questionSets: [] as QuestionSetDto[],
      resources: [] as ResourceDto[],
      companies: [] as CompanyDto[],
      products: [] as ProductDto[],
      productMap: {} as { [id: string]: ProductDto },
      laboratories: [] as TestLaboratoryDto[],
      covidTestAnalyzers: [] as CovidTestAnalyzerDto[],
      anonymousMode: true,
      referenceDataFetched: false,
      getTariffById: this.getTariffById,
      getReasonById: this.getReasonById,
      getQuestionSetById: this.getQuestionSetById,
      getInterventionById: this.getInterventionById,
      getInterventionByExternalCode: this.getInterventionByExternalCode,
      getTestKitReceptionOptionById: this.getTestKitReceptionOptionById,
      setAnonymousMode: this.setAnonymousMode
    };

    this.fetchReferenceData(props);
  }

  componentWillUnmount(): void {
    this.cancelTokenSource.cancel('Component unmounted.');
  }

  fetchReferenceData = (props: Props): void => {
    const {cancelTokenSource} = this;

    const {currentUser, getMandate} = props;
    const mandateId = getMandate();

    Promise.all([
          getAllTariffs(cancelTokenSource),
          getAllTreatmentReasons(cancelTokenSource),
          getAllInterventions(cancelTokenSource),
          getAllQuestionSets(cancelTokenSource),
          getResources(mandateId, cancelTokenSource),
          getAllCompanies(mandateId, cancelTokenSource),
          fetchAllProducts(false, cancelTokenSource),
          getAllTestLaboratory(cancelTokenSource),
          getAllCovidTestAnalyzers(cancelTokenSource),
          getAllTestKitReceptionOptions(cancelTokenSource)
        ])
        .then(result => {
          this.setState({
            referenceDataFetched: true,
            tariffs: result[0],
            reasons: result[1],
            interventions: result[2],
            questionSets: result[3],
            resources: result[4],
            companies: result[5],
            products: result[6],
            productMap: _.keyBy(result[6], "id"),
            laboratories: result[7],
            covidTestAnalyzers: result[8],
            testKitReceptionOptions: result[9]
          });
        });
  };

  getTariffById = (tariffId: number): TariffDto | undefined => {
    const {tariffs} = this.state;
    const tariff = tariffs.find(tariff => tariff.id === tariffId);
    return tariff;
  }

  getReasonById = (reasonId: number): TreatmentReasonDto | undefined => {
    const {reasons} = this.state;
    const reason = reasons.find(reason => reason.id === reasonId);
    return reason;
  }

  getQuestionSetById = (questionSetId: number): QuestionSetDto | undefined => {
    const {questionSets} = this.state;
    const questionSet = questionSets.find(qset => qset.id === questionSetId);
    return questionSet;
  }

  getInterventionById = (id: number): InterventionDto | undefined => {
    return this.state.interventions.find(intervention => intervention.id === id);
  }

  getTestKitReceptionOptionById = (id: number): TestKitReceptionOptionDto | undefined => {
    return this.state.testKitReceptionOptions.find(testKit => testKit.id === id);
  }

  getInterventionByExternalCode = (externalCode: InterventionExternalCode): InterventionDto | undefined => {
    return this.state.interventions.find(intervention => intervention.externalCode === externalCode);
  }

  setAnonymousMode = () => {
    this.setState({
      anonymousMode: !this.state.anonymousMode
    });
  }

  render() {
    return (
        <PatientCentralRefDataContext.Provider value={this.state}>
          {this.props.children}
        </PatientCentralRefDataContext.Provider>
    )
  }

}

export const PatientCentralRefDataContextProviderWrapper = withAuthContext(PatientCentralRefDataContextProvider);
export const PatientCentralRefDataConsumer = PatientCentralRefDataContext.Consumer;

export interface PatientCentralRefDataConsumerRenderProps extends State {
}

export const withPatientCentralRefDataContext = (Component: typeof React.Component | React.FunctionComponent<any>) => {
  return (props: any) => (
      <PatientCentralRefDataConsumer>
        {({...patientEntryProps}: PatientCentralRefDataConsumerRenderProps) => {
          return <Component {...props} {...patientEntryProps} />
        }}
      </PatientCentralRefDataConsumer>
  );
};
